import { isTemporaryOrderReadDelay } from './display-labels';

type Labeler = (value: string) => string;

function number(value: any) {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

function stageOf(role: string) {
    const match = String(role || '').match(/_(\d+)$/);
    return match ? number(match[1]) : 0;
}

function orderKind(order: any) {
    const role = String(order?.role || '');
    if (role === 'INITIAL_BUY') return 'entry';
    if (role.startsWith('ADD_BUY_')) return 'add';
    if (role.startsWith('REBUY_')) return 'rebuy';
    if (role.startsWith('PARTIAL_SELL_')) return 'sell';
    if (role.startsWith('RECYCLE_SELL_')) return 'recycle-sell';
    const side = String(order?.side || '').toUpperCase();
    if (side === 'BUY') return 'buy';
    if (side === 'SELL') return 'sell';
    return 'order';
}

function roleName(order: any, fallback: Labeler) {
    const role = String(order?.role || '');
    const stage = stageOf(role);
    if (role === 'INITIAL_BUY') return '첫 매수';
    if (role.startsWith('ADD_BUY_')) return stage ? `${stage}차 추가 매수` : '추가 매수';
    const addSell = role.match(/^ADD_SELL_(\d+)_(\d+)$/);
    if (addSell) return `추가 ${addSell[1]}차 물량 ${addSell[2]}차 매도`;
    if (role.startsWith('PARTIAL_SELL_')) return stage ? `${stage}차 분할 매도` : '분할 매도';
    if (role.startsWith('REBUY_')) return stage ? `${stage}차 재매수` : '재매수';
    if (role.startsWith('RECYCLE_SELL_')) {
        return stage ? `${stage}차 재매수분 매도` : '재매수분 매도';
    }
    return fallback(role);
}

function unknownDetail(reason: string) {
    const code = String(reason || '').toUpperCase();
    if (code.endsWith('_RATE_LIMIT')) {
        return 'KIS 요청이 잠시 제한되어 주문 상태를 다시 확인하고 있습니다.';
    }
    if (code.endsWith('_NETWORK')) {
        return 'KIS 응답이 지연되어 주문 상태를 다시 확인하고 있습니다.';
    }
    if (code.endsWith('_CREDENTIALS')) {
        return 'KIS 인증 상태를 다시 확인하고 있습니다.';
    }
    if (code.endsWith('_PROVIDER')) {
        return 'KIS 서버 응답을 다시 확인하고 있습니다.';
    }
    if (
        code.endsWith('_PAGINATION')
        || code.endsWith('_RESPONSE_SHAPE')
        || code.endsWith('_UNKNOWN')
    ) {
        return 'KIS 주문 조회 내용을 다시 확인하고 있습니다.';
    }
    return 'KIS 주문·체결 내역을 다시 확인하고 있습니다.';
}

function statusDetail(
    status: string,
    requested: number,
    filled: number,
    remaining: number,
    reason = ''
) {
    if (status === 'RESERVED') return '주문 전 안전 조건을 확인하고 있습니다.';
    if (status === 'SUBMITTING') return 'KIS에 주문을 접수하고 있습니다.';
    if (status === 'OPEN') return '주문이 접수되어 체결을 기다리고 있습니다.';
    if (status === 'PARTIALLY_FILLED') {
        return requested > 0
            ? `${filled}/${requested}주 체결 · ${remaining}주 대기 중입니다.`
            : `${filled}주 체결 · 남은 수량을 확인하고 있습니다.`;
    }
    if (status === 'CANCEL_PENDING') {
        return `남은 ${remaining}주의 취소 결과를 확인하고 있습니다.`;
    }
    if (status === 'UNKNOWN') return unknownDetail(reason);
    if (status === 'FILLED') return '전량 체결을 사이클에 반영하고 있습니다.';
    if (status === 'CANCELLED') return '취소 결과를 사이클에 반영하고 있습니다.';
    if (status === 'REJECTED') return '주문이 거절되어 원인을 확인하고 있습니다.';
    return '주문 체결 상태를 확인하고 있습니다.';
}

export function buildCycleOrderView(
    order: any,
    roleLabel: Labeler,
    statusLabel: Labeler
) {
    if (!order) return null;
    const status = String(order.status || 'UNKNOWN').toUpperCase();
    const requested = Math.max(0, number(order.requested_quantity));
    const filled = Math.max(0, number(order.filled_quantity));
    const storedRemaining = Math.max(0, number(order.remaining_quantity));
    const terminal = ['FILLED', 'CANCELLED', 'REJECTED'].includes(status);
    const remaining = terminal
        ? storedRemaining
        : storedRemaining || Math.max(0, requested - filled);
    return {
        kind: orderKind(order),
        label: roleName(order, roleLabel),
        status,
        statusLabel: isTemporaryOrderReadDelay(order) ? '조회 지연' : statusLabel(status),
        detail: statusDetail(
            status,
            requested,
            filled,
            remaining,
            String(order.reason || '')
        ),
        requested,
        filled,
        remaining,
        limitPrice: number(order.limit_price),
        averageFillPrice: number(order.average_fill_price),
        showFill: filled > 0 || ['PARTIALLY_FILLED', 'FILLED'].includes(status),
        tone: status === 'REJECTED'
            ? 'danger'
            : ['CANCEL_PENDING', 'UNKNOWN'].includes(status)
                ? 'warning'
                : status === 'PARTIALLY_FILLED' ? 'partial' : 'active'
    };
}
